// Josh swiped from https://github.com/ifeanyiisitor/react-use-scroll-direction/tree/main,
// which has not had to change any code in 5 years at time of writing.
// Then Brendan felt the need to make some changes:
// - removed the thing that resets the scroll direction to null since we were
//   overriding it for our singular use case
// - cleaned out left/right and scroll direction booleans
// at the first sign of trouble around here, go back to the original
import { useCallback, useEffect, useState } from "react";

/* eslint @typescript-eslint/no-unnecessary-condition: 0, @typescript-eslint/no-explicit-any: 0  */

type ListenerFn = () => any;

function getScrollTop(target?: HTMLElement) {
    /* v8 ignore if -- @preserve */
    if (target) return target.scrollTop;
    return (
        window.scrollY ||
        window.pageYOffset ||
        document.body.scrollTop ||
        (document.documentElement && document.documentElement.scrollTop) ||
        0
    );
}

function isBrowser() {
    return typeof window === "object";
}

function addScrollListener(
    listener: ListenerFn,
    target: HTMLElement | Document = document,
) {
    return target.addEventListener("scroll", listener, { passive: true });
}

function removeScrollListener(
    listener: ListenerFn,
    target: HTMLElement | Document = document,
) {
    return target.removeEventListener("scroll", listener);
}

export type ScrollDirection = "UP" | "DOWN" | null;

interface ScrollDirectionHookResult {
    scrollDirection: ScrollDirection;
    scrollTargetRef: (node: HTMLElement) => void;
}

export function useScrollDirection(
    target?: HTMLElement,
): ScrollDirectionHookResult {
    const [targetFromApi, setTargetFromApi] = useState<
        HTMLElement | undefined
    >();
    const [scrollDirection, setScrollDirection] =
        useState<ScrollDirection>(null);
    const targetToUse = target || targetFromApi;

    /* v8 ignore next 3 -- @preserve */
    const scrollTargetRef = useCallback((node: HTMLElement) => {
        setTargetFromApi(node);
    }, []);

    useEffect(() => {
        /* v8 ignore else -- @preserve */
        if (isBrowser()) {
            let lastScrollTop = getScrollTop(targetToUse);

            const handleScroll = () => {
                // Set vertical direction while scrolling
                const scrollTop = getScrollTop(targetToUse);
                if (scrollTop > lastScrollTop) {
                    setScrollDirection("DOWN");
                } else if (scrollTop < lastScrollTop) {
                    setScrollDirection("UP");
                }
                lastScrollTop = scrollTop;
            };

            addScrollListener(handleScroll, targetToUse);
            return () => removeScrollListener(handleScroll, targetToUse);
        }
    }, [targetToUse]);

    return {
        scrollDirection,
        scrollTargetRef,
    };
}
